import React, { useState, useEffect } from 'react';
import { Project } from '../types/billing.ts';
import api from '../services/api.ts';

interface ProjectsListProps {
  onViewProject: (id: string) => void;
}

const ProjectsList: React.FC<ProjectsListProps> = ({ onViewProject }) => {
  const [projects, setProjects] = useState<Project[]>([]);
  const [filteredProjects, setFilteredProjects] = useState<Project[]>([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [daysFilter, setDaysFilter] = useState(30);
  const [sortBy, setSortBy] = useState('total_cost');
  const [sortOrder, setSortOrder] = useState('desc');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);

  useEffect(() => {
    loadProjects();
  }, [currentPage, statusFilter, daysFilter, sortBy, sortOrder]);

  useEffect(() => {
    filterAndSortProjects();
  }, [projects, searchTerm]);

  const loadProjects = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await api.billing.getProjects({
        page: currentPage,
        limit: 50,
        days: daysFilter,
        project_status: statusFilter === 'all' ? undefined : statusFilter,
        sort_by: sortBy,
        sort_order: sortOrder
      });
      setProjects(response.projects);
      setTotalPages(response.pagination.pages);
    } catch (error) {
      console.error('Error loading projects:', error);
      setError('Failed to load projects. Please check if the API server is running.');
    } finally {
      setLoading(false);
    }
  };

  const filterAndSortProjects = () => {
    let filtered = projects.filter(project =>
      project.project_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      project.project_id.toLowerCase().includes(searchTerm.toLowerCase()) ||
      project.user_email?.toLowerCase().includes(searchTerm.toLowerCase())
    );

    setFilteredProjects(filtered);
  };

  const getStatusColor = (status: string) => {
    switch (status.toLowerCase()) {
      case 'active':
        return 'bg-green-400';
      case 'inactive':
        return 'bg-red-400';
      case 'archived':
        return 'bg-yellow-400';
      default:
        return 'bg-gray-400';
    }
  };

  const getStatusLabel = (status: string) => {
    switch (status.toLowerCase()) {
      case 'active':
        return 'Active';
      case 'inactive':
        return 'Inactive';
      case 'archived':
        return 'Archived';
      default:
        return status;
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-400"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-900 border border-red-700 rounded-lg p-6">
        <div className="flex items-center">
          <svg className="w-6 h-6 text-red-400 mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
          <div>
            <h3 className="text-lg font-medium text-red-400">Error Loading Projects</h3>
            <p className="text-red-300 mt-1">{error}</p>
            <button 
              onClick={loadProjects}
              className="mt-3 px-4 py-2 bg-red-700 text-white rounded hover:bg-red-600 transition-colors"
            >
              Retry
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center">
        <h1 className="text-2xl sm:text-3xl font-bold text-white mb-2 sm:mb-0">Projects Overview</h1>
        <div className="text-sm text-gray-400">
          {filteredProjects.length} projects found
        </div>
      </div>

      {/* Filters */}
      <div className="flex flex-col sm:flex-row gap-4">
        <div className="flex-1">
          <div className="relative">
            <input
              type="text"
              placeholder="Search projects, IDs, or user emails..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-3 bg-gray-800 border border-gray-700 rounded-lg text-white placeholder-gray-400 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            />
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center">
              <svg className="w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
              </svg>
            </div>
          </div>
        </div>
        <select
          value={statusFilter}
          onChange={(e) => {
            setStatusFilter(e.target.value);
            setCurrentPage(1);
          }}
          className="px-4 py-3 bg-gray-800 border border-gray-700 rounded-lg text-white focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
        >
          <option value="all">All Status</option>
          <option value="active">Active</option>
          <option value="inactive">Inactive</option>
          <option value="archived">Archived</option>
        </select>
        <select
          value={daysFilter}
          onChange={(e) => {
            setDaysFilter(Number(e.target.value));
            setCurrentPage(1);
          }}
          className="px-4 py-3 bg-gray-800 border border-gray-700 rounded-lg text-white focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
        >
          <option value={7}>Last 7 Days</option>
          <option value={30}>Last 30 Days (Current Cycle)</option>
          <option value={90}>Last 3 Months</option>
          <option value={365}>Last 1 Year</option>
        </select>
        <select
          value={`${sortBy}-${sortOrder}`}
          onChange={(e) => {
            const [newSortBy, newSortOrder] = e.target.value.split('-');
            setSortBy(newSortBy);
            setSortOrder(newSortOrder);
            setCurrentPage(1);
          }}
          className="px-4 py-3 bg-gray-800 border border-gray-700 rounded-lg text-white focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
        >
          <option value="total_cost-desc">Cost: High to Low</option>
          <option value="total_cost-asc">Cost: Low to High</option>
          <option value="project_name-asc">Name: A to Z</option>
          <option value="project_name-desc">Name: Z to A</option>
          <option value="created_at-desc">Newest First</option>
          <option value="created_at-asc">Oldest First</option>
        </select>
      </div>

      {/* Projects Grid */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6">
        {filteredProjects.map((project) => (
          <div 
            key={project.project_id} 
            className="bg-gray-800 border border-gray-700 rounded-xl p-4 sm:p-6 hover:bg-gray-700 cursor-pointer transition-all duration-200 hover:border-gray-600"
            onClick={() => onViewProject(project.project_id)}
          >
            <div className="flex items-start justify-between mb-4">
              <div className="flex items-center">
                <div className={`w-3 h-3 rounded-full mr-3 ${getStatusColor(project.status)}`}></div>
                <span className={`text-xs px-2 py-1 rounded-full ${
                  project.status === 'active' ? 'bg-green-900 text-green-400' : 
                  project.status === 'inactive' ? 'bg-red-900 text-red-400' : 
                  project.status === 'archived' ? 'bg-yellow-900 text-yellow-400' : 'bg-gray-900 text-gray-400'
                }`}>
                  {getStatusLabel(project.status)}
                </span>
              </div>
              <div className="text-sm text-gray-400">
                {project.services_used} services
              </div>
            </div>

            <h3 className="text-lg font-semibold text-white mb-2 truncate" title={project.project_name}>
              {project.project_name}
            </h3>
            <p className="text-sm text-gray-400 mb-3 truncate">{project.project_id}</p>

            {project.user_email && (
              <div className="mb-4 p-3 bg-gray-700 rounded-lg">
                <div className="flex items-center space-x-2">
                  <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                  </svg>
                  <p className="text-sm text-white truncate">{project.user_email}</p>
                </div>
              </div>
            )}

            <div className="grid grid-cols-2 gap-4">
              <div>
                <p className="text-sm text-gray-400">Total Cost</p>
                <p className="text-xl font-bold text-white">${project.total_cost.toFixed(2)}</p>
              </div>
              <div>
                <p className="text-sm text-gray-400">Credits</p>
                <p className="text-xl font-bold text-white">{project.total_credits?.toFixed(2) || '0.00'}</p>
              </div>
            </div>

            {project.last_activity && (
              <div className="mt-3 pt-3 border-t border-gray-700">
                <p className="text-xs text-gray-400">
                  Last activity: {new Date(project.last_activity).toLocaleDateString()}
                </p>
              </div>
            )}
          </div>
        ))}
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex justify-center items-center space-x-2">
          <button
            onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
            disabled={currentPage === 1}
            className="px-3 py-2 bg-gray-800 border border-gray-700 rounded text-white hover:bg-gray-700 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Previous
          </button>
          <span className="text-white">
            Page {currentPage} of {totalPages}
          </span>
          <button
            onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
            disabled={currentPage === totalPages}
            className="px-3 py-2 bg-gray-800 border border-gray-700 rounded text-white hover:bg-gray-700 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Next
          </button>
        </div>
      )}

      {filteredProjects.length === 0 && !loading && (
        <div className="text-center py-12">
          <svg className="mx-auto h-12 w-12 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
          </svg>
          <h3 className="mt-2 text-sm font-medium text-gray-400">No projects found</h3>
          <p className="mt-1 text-sm text-gray-500">
            {projects.length === 0 ? 'No projects available in the system.' : 'Try adjusting your search criteria.'}
          </p>
        </div>
      )}
    </div>
  );
};

export default ProjectsList;